import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Code2, Copy, Check, FileCode, Search, Loader2, Download } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import PadaidLogo from '../../../components/ui/PadaidLogo';
import { getCodeTree, getCodeFile } from '../../../services/code';

const fmtSize = (n) => (n > 1024 ? `${(n / 1024).toFixed(1)} Ko` : `${n} o`);

const CodeBrowser = () => {
  const [files, setFiles] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(null);
  const [content, setContent] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await getCodeTree();
        setFiles(data.files || []);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally { setLoadingTree(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? files.filter((f) => f.path.toLowerCase().includes(q)) : files;
  }, [files, query]);

  const openFile = async (path) => {
    setActive(path); setLoadingFile(true); setError(''); setCopied(false);
    try {
      const data = await getCodeFile(path);
      setContent(data.content || '');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setContent('');
    } finally { setLoadingFile(false); }
  };

  const copyAll = () => {
    navigator.clipboard?.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const download = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = (active || 'fichier').split('/').pop();
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5" data-testid="code-browser">
      <header className="bg-card/50 backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <PadaidLogo size="md" showText={false} />
            <div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Code2 className="h-4 w-4" /> Espace fondateur</div>
              <h1 className="text-xl font-bold">Code source</h1>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild data-testid="code-back">
            <Link to="/dashboard/fondateur/admin"><ArrowLeft className="h-4 w-4 mr-2" />Centre d'administration</Link>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-sm text-muted-foreground mb-4">
          Parcourez le code de l'application (lecture seule, secrets exclus). Ouvrez un fichier, copiez-le, et transmettez-le pour demander un ajout ou une suppression.
        </p>
        {error && <div className="mb-4 rounded-md bg-red-500/10 text-red-600 px-4 py-2 text-sm" data-testid="code-error">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Liste des fichiers */}
          <Card className="lg:col-span-1 h-[70vh] flex flex-col">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Rechercher un fichier…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="code-search" />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">{filtered.length} fichier(s)</div>
            </div>
            <div className="flex-1 overflow-auto p-2">
              {loadingTree ? (
                <div className="flex items-center justify-center h-full text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…</div>
              ) : filtered.map((f) => (
                <button
                  key={f.path}
                  onClick={() => openFile(f.path)}
                  className={`w-full text-left px-2 py-1.5 rounded-md text-xs font-mono flex items-center justify-between gap-2 transition-colors ${active === f.path ? 'bg-primary/15 text-primary' : 'hover:bg-muted'}`}
                  data-testid={`code-file-${f.path}`}
                >
                  <span className="flex items-center gap-1.5 truncate"><FileCode className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{f.path}</span></span>
                  <span className="text-[10px] text-muted-foreground shrink-0">{fmtSize(f.size)}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Visualiseur */}
          <Card className="lg:col-span-2 h-[70vh] flex flex-col">
            <div className="p-3 border-b border-border flex items-center justify-between gap-2">
              <div className="font-mono text-sm truncate" data-testid="code-active-path">{active || 'Aucun fichier sélectionné'}</div>
              {active && (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={copyAll} data-testid="code-copy-btn">
                    {copied ? <><Check className="h-3.5 w-3.5 mr-1 text-emerald-500" />Copié</> : <><Copy className="h-3.5 w-3.5 mr-1" />Copier</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={download} data-testid="code-download-btn"><Download className="h-3.5 w-3.5 mr-1" />Télécharger</Button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-auto bg-muted/30">
              {loadingFile ? (
                <div className="flex items-center justify-center h-full text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Ouverture…</div>
              ) : active ? (
                <pre className="p-4 text-xs font-mono whitespace-pre overflow-auto"><code data-testid="code-content">{content}</code></pre>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Sélectionnez un fichier à gauche pour afficher son contenu.</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CodeBrowser;
